import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import ClustrLogo from "@/components/icons/ClustrLogo";
import Footer from "@/components/landing/Footer";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

const PrivacyPolicy = () => {
  const { title, description, image } = APP_METADATA.privacy;
  useMetadata({ title, description, image });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="relative">
        <div className="w-full">
          <img 
            src="/lovable-uploads/a1e72f38-b4de-469b-94ba-469310654ec8.png"
            alt="Clustr Privacy Visualization" 
            className="w-full object-contain"
          />
        </div>
        
        <div className="absolute top-4 left-0 w-full px-6">
          <div className="max-w-6xl mx-auto">
            <Link to="/">
              <ClustrLogo 
                width={180} 
                height={54} 
                src="/lovable-uploads/abf9b449-c10d-4d99-b03c-c106652be5bb.png"
              />
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full px-6 pb-6 bg-gradient-to-t from-white via-white/80 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-5">
              <Shield className="h-20 w-20 text-clustr-blue" />
              <h1 className="text-6xl font-bold text-clustr-text">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">Clustr Privacy Policy</h2>
            
            <h3 className="text-xl font-semibold mb-4">Overview</h3>
            <p className="text-gray-700 mb-4">
              At Clustr, we believe in complete transparency about how our Web Clipper extension ("Extension") and service handle your data. This Privacy Policy describes how we collect, use, and protect information when you use our browser extension and web application. Our core principle is that your data belongs to you, and we only process information that you explicitly choose to save.
            </p>

            <h3 className="text-xl font-semibold mb-4">Our Privacy Commitments</h3>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li>We only access tab information when you explicitly click the extension</li>
              <li>We never track your browsing history or behavior</li>
              <li>We don't collect any analytics or usage data</li>
              <li>All data processing happens locally in your browser</li>
              <li>Data is only transmitted to your personal Clustr account when you choose to save content</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">Information We Process</h3>
            <p className="text-gray-700 mb-2">When you explicitly choose to save content, we collect:</p>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li>The specific web page content you select to save (web pages, text selections, images)</li>
              <li>Basic metadata needed for organization (titles, URLs, timestamps)</li>
              <li>Any optional notes or tags you add</li>
              <li>A thumbnail image if available on the page</li>
              <li>Authentication information to connect to your Clustr account</li>
              <li>Extension settings and preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">How We Use Information</h3>
            <p className="text-gray-700 mb-2">We use this information exclusively to:</p>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li>Save content to your personal Clustr collections</li>
              <li>Organize and display your saved content</li>
              <li>Maintain and improve the extension functionality</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">How We Use Permissions</h3>
            <p className="text-gray-700 mb-2">The extension requires minimal permissions:</p>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li><strong>activeTab:</strong> Only activated when you click the extension icon to save content</li>
              <li><strong>storage:</strong> Stores your preferences locally, never shared externally</li>
              <li><strong>scripting:</strong> Allows content extraction only with your direct consent</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">Data Storage and Security</h3>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li>All content you save is stored in your personal Clustr account</li>
              <li>Authentication tokens are stored securely in your browser's local storage</li>
              <li>We use industry-standard encryption for data transmission</li>
              <li>We never share or sell your saved content to third parties</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">Your Data Rights</h3>
            <p className="text-gray-700 mb-2">You have full control over your data. You can:</p>
            <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-2">
              <li>Access all content saved to your Clustr account at any time</li>
              <li>Modify or delete individual saved items</li>
              <li>Export your data in standard formats (CSV)</li>
              <li>Request deletion of your entire account and associated data</li>
              <li>Request a copy of all your data</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
            <p className="text-gray-700 mb-4">
              For questions about your privacy or this policy, please contact us at:<br />
              Email: <a href="mailto:contact@ux-flo.com" className="text-clustr-blue hover:underline">
                contact@ux-flo.com
              </a>
            </p>

            <h3 className="text-xl font-semibold mb-4">Changes to This Policy</h3>
            <p className="text-gray-700 mb-4">
              This Privacy Policy was last updated on: April 21, 2025<br /><br />
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal reasons. We will notify you of any material changes by posting the new Privacy Policy on our website and updating the "Last Updated" date.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
